import {
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useMap } from "../../map/useMapStore";
import { jumpTo } from "../../map/jumpTo";
import type {
  CommunePoint,
  CorrelationX,
  CorrelationY,
} from "../../lib/demographics";
import { xMetric, yMetric } from "./metrics";

/**
 * Every city in the region as one dot: how it is changing against what it costs.
 *
 * The point of the pane, and the one view that answers "does any of this show
 * up in prices". A dot is a city, so clicking one flies the map to it and
 * opens it — an outlier is only interesting once you know which place it is.
 *
 * The line is an ordinary least-squares fit, drawn so the direction of the
 * relationship is visible even when the cloud is wide, which it usually is.
 */
export function CorrelationScatter({
  points,
  x,
  y,
  selectedCode,
}: {
  points: CommunePoint[];
  x: CorrelationX;
  y: CorrelationY;
  selectedCode: string | null;
}) {
  const map = useMap();
  const mx = xMetric(x);
  const my = yMetric(y);

  const data = points.map((p) => ({ ...p, x: p[x], y: p[y] }));
  const selected = data.filter((p) => p.code === selectedCode);
  const fit = leastSquares(data);

  // A handful of small communes have wild price trends — five sales a year is
  // enough to publish a median and not enough to fit one — and left to set the
  // axis they squash every other city into a band two pixels tall. The axis is
  // cut to the bulk of the distribution and says how many fell outside it.
  const domain = robustDomain(data.map((d) => d.y));
  const outside = data.filter((d) => d.y < domain[0] || d.y > domain[1]).length;

  return (
    <>
      <div className="h-44 w-full">
        <ResponsiveContainer>
        <ScatterChart margin={{ top: 6, right: 8, bottom: 2, left: -4 }}>
          <CartesianGrid stroke="#e3e7ed" strokeDasharray="2 3" />
          <XAxis
            type="number"
            dataKey="x"
            name={mx.axis}
            tick={{ fontSize: 9, fill: "#5a6472" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={mx.format}
            domain={["dataMin", "dataMax"]}
          />
          <YAxis
            type="number"
            dataKey="y"
            name={my.axis}
            tick={{ fontSize: 9, fill: "#5a6472" }}
            axisLine={false}
            tickLine={false}
            width={44}
            tickFormatter={my.format}
            domain={domain}
            allowDataOverflow
          />
          {/* Zero is a real boundary on every rate metric — shrinking and
              growing are different states, not two ends of one scale. */}
          {x !== "density" && x !== "vacancy_rate" && x !== "second_rate" && (
            <ReferenceLine x={0} stroke="#5a6472" strokeOpacity={0.4} />
          )}
          {fit && (
            <ReferenceLine
              segment={fit}
              stroke="#2f6df6"
              strokeWidth={1.5}
              strokeOpacity={0.7}
              ifOverflow="hidden"
            />
          )}
          <Tooltip
            cursor={{ strokeDasharray: "2 3" }}
            contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e3e7ed" }}
            content={({ payload }) => {
              const p = payload?.[0]?.payload as (typeof data)[number] | undefined;
              if (!p) return null;
              return (
                <div className="rounded-lg border border-line bg-surface px-2 py-1.5 text-[11px] shadow-sm">
                  <div className="font-medium">{p.name}</div>
                  <div className="tabular-nums text-ink-soft">
                    {mx.format(p.x)} · {my.format(p.y)}
                  </div>
                </div>
              );
            }}
          />
          <Scatter
            data={data}
            fill="#5a6472"
            fillOpacity={0.45}
            shape="circle"
            isAnimationActive={false}
            onClick={(p: unknown) => {
              const point = p as CommunePoint;
              if (map && point.lon) {
                // Close enough that the commune fills the view, which is what
                // makes the click on the map underneath select that commune.
                jumpTo(map, { lon: point.lon, lat: point.lat, zoom: 12 });
              }
            }}
            style={{ cursor: map ? "pointer" : "default" }}
          />
          {selected.length > 0 && (
            <Scatter
              data={selected}
              isAnimationActive={false}
              // Drawn by hand rather than as a bigger circle: a ring reads as
              // "this one" in a cloud of a few hundred grey dots, where a
              // slightly larger dot does not.
              shape={(props: { cx?: number; cy?: number }) => (
                <circle
                  cx={props.cx}
                  cy={props.cy}
                  r={5}
                  fill="#14181f"
                  stroke="#ffffff"
                  strokeWidth={2}
                />
              )}
            />
          )}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      {outside > 0 && (
        <p className="mt-0.5 text-right text-[9px] text-ink-soft">
          {outside} {outside === 1 ? "city sits" : "cities sit"} off the scale
        </p>
      )}
    </>
  );
}

/** Second to ninety-eighth percentile, always including zero. */
function robustDomain(values: number[]): [number, number] {
  if (values.length === 0) return [0, 1];
  const sorted = [...values].sort((a, b) => a - b);
  const at = (q: number) => sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))];
  const lo = Math.min(0, at(0.02));
  const hi = Math.max(0, at(0.98));
  const pad = (hi - lo) * 0.06 || 1;
  return [lo - pad, hi + pad];
}

/** Endpoints of the least-squares line, for recharts' `segment`. */
function leastSquares(
  data: { x: number; y: number }[],
): [{ x: number; y: number }, { x: number; y: number }] | null {
  if (data.length < 3) return null;
  const n = data.length;
  const mx = data.reduce((a, p) => a + p.x, 0) / n;
  const my = data.reduce((a, p) => a + p.y, 0) / n;
  let num = 0;
  let den = 0;
  for (const p of data) {
    num += (p.x - mx) * (p.y - my);
    den += (p.x - mx) ** 2;
  }
  if (den === 0) return null;
  const slope = num / den;
  const lo = Math.min(...data.map((p) => p.x));
  const hi = Math.max(...data.map((p) => p.x));
  return [
    { x: lo, y: my + slope * (lo - mx) },
    { x: hi, y: my + slope * (hi - mx) },
  ];
}
